/* tslint:disable:max-file-line-count */
import { useApolloClient, useMutation } from '@apollo/client';
import { firstOccurrence } from '@focuslee/shared';
import dayjs from 'dayjs';
import { useCallback } from 'react';
import {
    CreateAchievementMutation,
    CreateAchievementMutationVariables,
    CreateCategoryMutation,
    CreateCategoryMutationVariables,
    CreateQuestMutation,
    CreateQuestMutationVariables,
    QuestDifficulty,
    QuestMetricType,
    RepeatingType,
    UpdateQuestDateRepeatMutation,
    UpdateQuestDateRepeatMutationVariables,
} from '../client/generated/graphql';
import { createAchievementCache, createAchievementMutation } from '../client/mutations/createAchievement';
import { createCategoryCache, createCategoryMutation } from '../client/mutations/createCategory';
import { createQuestCache, createQuestMutation } from '../client/mutations/createQuest';
import { updateQuestDateRepeatCache, updateQuestDateRepeatMutation } from '../client/mutations/updateQuestDateRepeat';
import { ListCategory, ListQuest } from '../client/types';
import { findCategoryByName } from '../helpers/category';
import { getRandomColor, resolveColorFromKeyword } from '../helpers/color';
import { getRandomIcon } from '../helpers/common';
import { findQuestByName, getRepeatFromStr, getVoicePostponeDate } from '../helpers/quest';
import { voiceActionErrorContext } from '../helpers/voiceActionDisplay';
import { readCachedCategories } from '../hooks/caching/category';
import { readCachedQuests } from '../hooks/caching/quest';
import {
    type CreateAchievementVoiceAction,
    type CreateCategoryVoiceAction,
    type CreateQuestVoiceAction,
    type PostponeQuestVoiceAction,
    type VoiceAction,
} from '../types/voice';

type ProcessActionContext = {
    allCategories: ListCategory[];
    createQuest: ReturnType<typeof useMutation<CreateQuestMutation, CreateQuestMutationVariables>>[0];
    createCategory: ReturnType<typeof useMutation<CreateCategoryMutation, CreateCategoryMutationVariables>>[0];
    createAchievement: ReturnType<typeof useMutation<CreateAchievementMutation, CreateAchievementMutationVariables>>[0];
    updateQuestDateRepeat: ReturnType<typeof useMutation<UpdateQuestDateRepeatMutation, UpdateQuestDateRepeatMutationVariables>>[0];
    cachedQuests: ListQuest[];
};

type QuestCreateOutcome = { questId: string; categoryId: string };

function sortVoiceActionsForExecution(actions: VoiceAction[]): VoiceAction[] {
    return [...actions.filter(a => !a.dependsOnActionIds?.length), ...actions.filter(a => a.dependsOnActionIds?.length)];
}

async function processCreateQuestAction(
    action: CreateQuestVoiceAction,
    ctx: ProcessActionContext,
): Promise<{ success: true; outcome?: QuestCreateOutcome } | { success: false; error: string }> {
    const category = action.categoryId
        ? ctx.allCategories.find(c => c.id === action.categoryId)
        : findCategoryByName(ctx.allCategories, action.categoryName ?? '') ?? ctx.allCategories[0];
    if (!category) {
        return { success: false, error: `Category not found for "${action.questName}"` };
    }

    const repeatResult = action.repeatQuery ? getRepeatFromStr(action.repeatQuery) : undefined;
    let dueDate: Date | null = action.dueDateIso
        ? new Date(action.dueDateIso)
        : repeatResult
        ? firstOccurrence(repeatResult[1]).toDate()
        : null;
    if (dueDate && repeatResult?.[1].startTime) {
        const st = repeatResult[1].startTime;
        dueDate = dayjs(dueDate)
            .hour(st.hours)
            .minute(st.minutes ?? 0)
            .second(0)
            .millisecond(0)
            .toDate();
    }
    const repeatInput = repeatResult
        ? {
              query: repeatResult[0].query,
              repeatingType: repeatResult[0].repeatingType as RepeatingType,
              interval: repeatResult[0].interval,
              weekDays: repeatResult[0].weekDays ?? [],
              monthDays: repeatResult[0].monthDays ?? [],
              startHour: repeatResult[0].startHour,
              startMinute: repeatResult[0].startMinute,
          }
        : undefined;
    const difficulty =
        action.difficulty && QuestDifficulty[action.difficulty as keyof typeof QuestDifficulty]
            ? QuestDifficulty[action.difficulty as keyof typeof QuestDifficulty]
            : QuestDifficulty.NoEffort;
    const timing = action.focusModeEnabled ?? false;
    const timedInterval = action.timedIntervalMinutes ?? 0;

    const res = await ctx.createQuest({
        variables: {
            name: action.questName,
            description: action.description ?? '',
            categoryId: category.id,
            difficulty,
            timing,
            timedInterval,
            timerValueInMs: 0,
            dueDate: dueDate ? dueDate.toISOString() : null,
            repeat: repeatInput,
        },
    });
    const questId = res.data?.createQuest?.id;
    if (!questId) {
        return { success: false, error: `Create quest failed for "${action.questName}"` };
    }
    return { success: true, outcome: { questId, categoryId: category.id } };
}

async function processCreateCategoryAction(
    action: CreateCategoryVoiceAction,
    ctx: ProcessActionContext,
): Promise<{ success: true } | { success: false; error: string }> {
    const icon = action.iconName?.trim() || getRandomIcon();
    const iconColor = action.iconColorName ? resolveColorFromKeyword(action.iconColorName) : getRandomColor();
    await ctx.createCategory({
        variables: {
            name: action.categoryName,
            description: action.description ?? '',
            icon,
            iconColor,
            parentId: null,
            goalEnabled: false,
        },
    });
    return { success: true };
}

type ResolveAchievementLinkedQuestIdsResult =
    | { success: true; linkedQuestIds: string[]; categoryId: string | undefined }
    | { success: false; error: string };

function resolveAchievementLinkedQuestIds(
    action: CreateAchievementVoiceAction,
    ctx: ProcessActionContext,
    questOutcomeByVoiceActionId: Map<string, QuestCreateOutcome>,
): ResolveAchievementLinkedQuestIdsResult {
    const createdVoiceActionIds = action.dependsOnActionIds ?? [];

    if (createdVoiceActionIds.length) {
        const outcomes = createdVoiceActionIds.map(id => questOutcomeByVoiceActionId.get(id));
        if (outcomes.some(o => !o)) {
            return {
                success: false,
                error: `Achievement "${action.achievementName}" depends on a quest that was not created`,
            };
        }
        const linkedQuestIds = outcomes.map(o => o!.questId);
        const categoryId = outcomes[0]?.categoryId;
        return { success: true, linkedQuestIds, categoryId };
    }

    if (action.questIds?.length) {
        const linkedQuestIds = [...action.questIds];
        const first = ctx.cachedQuests.find(q => q.id === linkedQuestIds[0]);
        return {
            success: true,
            linkedQuestIds,
            categoryId: first?.categoryId ?? action.categoryId,
        };
    }

    if (action.questNames?.length) {
        const rows = action.questNames.map(name => {
            const q = findQuestByName(ctx.cachedQuests, name);
            return q ? { id: q.id, categoryId: q.categoryId } : null;
        });
        const missIdx = rows.findIndex(r => !r);
        if (missIdx !== -1) {
            return {
                success: false,
                error: `Quest "${action.questNames[missIdx]}" not found for achievement "${action.achievementName}"`,
            };
        }
        const linkedQuestIds = rows.map(r => r!.id);
        const categoryId = rows.reduce<string | undefined>((acc, r) => acc ?? r?.categoryId, undefined);
        return { success: true, linkedQuestIds, categoryId };
    }

    return { success: true, linkedQuestIds: [], categoryId: undefined };
}

async function processCreateAchievementAction(
    action: CreateAchievementVoiceAction,
    ctx: ProcessActionContext,
    questOutcomeByVoiceActionId: Map<string, QuestCreateOutcome>,
): Promise<{ success: true } | { success: false; error: string }> {
    const metricType = action.conditionMetricType ?? QuestMetricType.Completion;
    const target = metricType === QuestMetricType.Completion ? null : action.conditionTarget ?? null;
    const conditionQuestIds = action.conditionQuestIds ?? [];

    const linked = resolveAchievementLinkedQuestIds(action, ctx, questOutcomeByVoiceActionId);
    if (!linked.success) return { success: false, error: linked.error };

    const questIds = [...new Set([...linked.linkedQuestIds, ...conditionQuestIds])];
    if (questIds.length === 0) {
        return { success: false, error: `Achievement "${action.achievementName}" needs a quest` };
    }

    const categoryId =
        action.categoryId ??
        linked.categoryId ??
        findCategoryByName(ctx.allCategories, action.categoryName ?? '')?.id ??
        ctx.cachedQuests.find(q => q.id === questIds[0])?.categoryId;

    if (!categoryId) {
        return { success: false, error: `Category not found for achievement "${action.achievementName}"` };
    }

    await ctx.createAchievement({
        variables: {
            name: action.achievementName,
            description: action.description ?? '',
            categoryId,
            rarity: action.rarity,
            icon: action.icon,
            achievementConditions: [{ metricType, target, questIds }],
        },
    });
    return { success: true };
}

async function processPostponeQuestAction(
    action: PostponeQuestVoiceAction,
    ctx: ProcessActionContext,
): Promise<{ success: true } | { success: false; error: string }> {
    const hasQuestIdAndDue = action.questId && action.dueDateIso && dayjs(action.dueDateIso).isValid();
    if (hasQuestIdAndDue) {
        await ctx.updateQuestDateRepeat({
            variables: { questId: action.questId!, dueDate: action.dueDateIso!, repeat: undefined },
            update: updateQuestDateRepeatCache,
        });
        return { success: true };
    }

    const quest = action.questId
        ? ctx.cachedQuests.find(q => q.id === action.questId)
        : findQuestByName(ctx.cachedQuests, action.questName);
    if (!quest) {
        return { success: false, error: action.questName ? `Quest "${action.questName}" not found` : 'Quest not found' };
    }

    const dueDate =
        action.dueDateIso && dayjs(action.dueDateIso).isValid() ? new Date(action.dueDateIso) : getVoicePostponeDate(quest).toDate();

    await ctx.updateQuestDateRepeat({
        variables: { questId: quest.id, dueDate: dueDate.toISOString(), repeat: undefined },
        update: updateQuestDateRepeatCache,
    });
    return { success: true };
}

async function processAction(
    action: VoiceAction,
    ctx: ProcessActionContext,
    questOutcomeByVoiceActionId: Map<string, QuestCreateOutcome>,
): Promise<{ success: true } | { success: false; error: string }> {
    switch (action.type) {
        case 'CREATE_QUEST': {
            const r = await processCreateQuestAction(action, ctx);
            if (r.success && r.outcome) questOutcomeByVoiceActionId.set(action.id, r.outcome);
            return r.success ? { success: true } : r;
        }
        case 'CREATE_CATEGORY':
            return processCreateCategoryAction(action, ctx);
        case 'POSTPONE_QUEST':
            return processPostponeQuestAction(action, ctx);
        case 'CREATE_ACHIEVEMENT':
            return processCreateAchievementAction(action, ctx, questOutcomeByVoiceActionId);
    }
}

export const useVoiceActions = () => {
    const client = useApolloClient();

    const [createQuest] = useMutation<CreateQuestMutation, CreateQuestMutationVariables>(createQuestMutation, {
        update: createQuestCache,
    });
    const [createCategory] = useMutation<CreateCategoryMutation, CreateCategoryMutationVariables>(createCategoryMutation, {
        update: createCategoryCache,
    });
    const [updateQuestDateRepeat] = useMutation<UpdateQuestDateRepeatMutation, UpdateQuestDateRepeatMutationVariables>(
        updateQuestDateRepeatMutation,
    );
    const [createAchievement] = useMutation<CreateAchievementMutation, CreateAchievementMutationVariables>(createAchievementMutation, {
        update: createAchievementCache,
    });

    const execute = useCallback(
        async (actions: VoiceAction[]): Promise<{ success: number; errors: string[] }> => {
            const allCategories = readCachedCategories(client);
            const cachedQuests = readCachedQuests(client);
            const errors: string[] = [];
            let success = 0;
            const ctx: ProcessActionContext = {
                allCategories,
                createQuest,
                createCategory,
                createAchievement,
                updateQuestDateRepeat,
                cachedQuests,
            };
            const questOutcomeByVoiceActionId = new Map<string, QuestCreateOutcome>();
            const ordered = sortVoiceActionsForExecution(actions);

            for (const action of ordered) {
                try {
                    const result = await processAction(action, ctx, questOutcomeByVoiceActionId);
                    if (result.success) success++;
                    else errors.push(result.error);
                } catch (e) {
                    const label = voiceActionErrorContext(action);
                    errors.push(`${action.type} "${label}": ${e instanceof Error ? e.message : 'Unknown error'}`);
                }
            }

            return { success, errors };
        },
        [client, createQuest, createCategory, createAchievement, updateQuestDateRepeat],
    );

    return { execute };
};
